export type DescriptionSettingItem = {
  id?: number | string | null;
  value?: string | null;
  label?: string | null;
};

export type AccountDescriptionSettings = {
  orderingDescription?: DescriptionSettingItem[] | null;
  salesDescriptionCom?: DescriptionSettingItem[] | null;
  salesDescriptionWorkroom?: DescriptionSettingItem[] | null;
};

export type DescriptionValueMap = Record<string, string>;

type DescriptionFormFields = {
  supplierName?: string | null;
  customerFacingName?: string | null;
  name?: string | null;
  itemNumber?: string | null;
};

type DescriptionDetailsFields = {
  fabricWidthInches?: string | number | null;
  verticalRepeatInches?: string | number | null;
  horizontalRepeatInches?: string | number | null;
};

type BuildItemDescriptionsArgs = {
  descriptionSettings?: AccountDescriptionSettings | null;
  formFields?: DescriptionFormFields | null;
  detailsFields?: DescriptionDetailsFields | null;
  isFabricsOrLiningsCategory?: boolean;
};

const DEFAULT_ORDERING_DESCRIPTION = [
  { value: 'name' },
  { value: 'item' },
  { value: 'specifications' },
];

const DEFAULT_SALES_DESCRIPTION = [
  { value: 'supplier' },
  { value: 'customer-facing-name' },
  { value: 'item' },
  { value: 'specifications' },
];

export const ORDERING_DESCRIPTION_SETTINGS_ORDER = [
  'supplier',
  'name',
  'item',
  'specifications',
];

export const SALES_DESCRIPTION_SETTINGS_ORDER = [
  'supplier',
  'customer-facing-name',
  'name',
  'item',
  'specifications',
];

const hasValue = (value?: string | number | null): boolean =>
  value !== undefined && value !== null && `${value}`.trim().length > 0;

export const sortDescriptionSettings = (
  settings: DescriptionSettingItem[] | null | undefined,
  orderedValues: string[]
): DescriptionSettingItem[] => {
  if (!Array.isArray(settings)) {
    return [];
  }

  const rankByValue = new Map(orderedValues.map((value, index) => [value, index]));

  return [...settings].sort((a, b) => {
    const rankA = rankByValue.get(a?.value || '') ?? Number.MAX_SAFE_INTEGER;
    const rankB = rankByValue.get(b?.value || '') ?? Number.MAX_SAFE_INTEGER;

    if (rankA !== rankB) {
      return rankA - rankB;
    }

    return Number(a?.id || 0) - Number(b?.id || 0);
  });
};

export const getDescriptionDimensionsPreview = (
  detailsFields?: DescriptionDetailsFields | null
): string => {
  const parts = [
    hasValue(detailsFields?.fabricWidthInches) ? `W: ${detailsFields?.fabricWidthInches}"` : '',
    hasValue(detailsFields?.verticalRepeatInches)
      ? `VR: ${detailsFields?.verticalRepeatInches}"`
      : '',
    hasValue(detailsFields?.horizontalRepeatInches)
      ? `HR: ${detailsFields?.horizontalRepeatInches}"`
      : '',
  ].filter(Boolean);

  return parts.join(' / ');
};

const getDescriptionValueMap = ({
  formFields,
  detailsFields,
  isFabricsOrLiningsCategory,
}: Pick<BuildItemDescriptionsArgs, 'formFields' | 'detailsFields' | 'isFabricsOrLiningsCategory'>) => {
  const customerFacingName = formFields?.customerFacingName?.trim() || '';
  const dimensionsPreview = isFabricsOrLiningsCategory
    ? getDescriptionDimensionsPreview(detailsFields)
    : '';

  return {
    supplier: formFields?.supplierName || '',
    'customer-facing-name': customerFacingName,
    name: formFields?.name || '',
    item: formFields?.itemNumber || '',
    specifications: dimensionsPreview,
  };
};

export const resolveDescriptionSettingLines = ({
  settings,
  valueMap,
  orderedValues,
  fallbackSettings,
}: {
  settings: DescriptionSettingItem[] | null | undefined;
  valueMap: DescriptionValueMap;
  orderedValues: string[];
  fallbackSettings: DescriptionSettingItem[];
}): string[] => {
  const activeSettings = Array.isArray(settings)
    ? sortDescriptionSettings(settings, orderedValues)
    : fallbackSettings;
  const selectedKeys = new Set(activeSettings.map((setting) => setting?.value || ''));

  return activeSettings
    .map((setting) => {
      const key = setting?.value || '';

      if (key === 'customer-facing-name') {
        return valueMap['customer-facing-name'] || valueMap.name || '';
      }

      if (key === 'name' && selectedKeys.has('customer-facing-name')) {
        return '';
      }

      return valueMap[key] || '';
    })
    .filter((value) => hasValue(value));
};

const buildDescriptionString = (
  settings: DescriptionSettingItem[] | null | undefined,
  valueMap: Record<string, string>,
  fallbackSettings: DescriptionSettingItem[],
  orderedValues: string[]
): string => {
  return resolveDescriptionSettingLines({
    settings,
    valueMap,
    orderedValues,
    fallbackSettings,
  }).join('\n');
};

export const buildItemDescriptionsFromSettings = ({
  descriptionSettings,
  formFields,
  detailsFields,
  isFabricsOrLiningsCategory = false,
}: BuildItemDescriptionsArgs) => {
  const valueMap = getDescriptionValueMap({
    formFields,
    detailsFields,
    isFabricsOrLiningsCategory,
  });

  return {
    salesDescription: buildDescriptionString(
      descriptionSettings?.salesDescriptionCom,
      valueMap,
      DEFAULT_SALES_DESCRIPTION,
      SALES_DESCRIPTION_SETTINGS_ORDER
    ),
    salesDescriptionWorkroom: buildDescriptionString(
      descriptionSettings?.salesDescriptionWorkroom,
      valueMap,
      DEFAULT_SALES_DESCRIPTION,
      SALES_DESCRIPTION_SETTINGS_ORDER
    ),
    orderingDescription: buildDescriptionString(
      descriptionSettings?.orderingDescription,
      valueMap,
      DEFAULT_ORDERING_DESCRIPTION,
      ORDERING_DESCRIPTION_SETTINGS_ORDER
    ),
  };
};
